import { useRef, useState } from "react";
import { saveProfilePhoto } from "./utils/indexedDB";

export default function Onboarding({ onDone }) {
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [dobError, setDobError] = useState("");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [photo, setPhoto] = useState("");
  const [userClass, setUserClass] = useState("");

  const [error, setError] = useState(false);
  const [showClassInfo, setShowClassInfo] = useState(false);

  const fileRef = useRef(null);

  const isTouchDevice =
    typeof window !== "undefined" &&
    ("ontouchstart" in window || navigator.maxTouchPoints > 0);

  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 ? String(age) : "";
  };

  const submit = async () => {
    if (!name.trim() || userClass === "" || !gender) {
      setError(true);
      alert(
        "Please complete all required fields:\n\n• Name\n• Class\n• Gender"
      );
      return;
    }
    if (email && emailError) {
      alert("Please enter a valid email address.");
      return;
    }

    const profile = {
      Name: name,
      Class: Number(userClass), // ✅ FIX
      Gender: gender,
      DOB: dob,
      Age: calculateAge(dob),
      Email: email,
    };

    localStorage.setItem("profileData", JSON.stringify(profile));

    if (photo) {
      const blob = await fetch(photo).then((r) => r.blob());
      await saveProfilePhoto(blob);
    }

    localStorage.setItem("onboardingDone", "true");
    onDone();
  };

  const onPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const blob = new Blob([buffer], { type: file.type });

    // preview in onboarding UI
    setPhoto(URL.createObjectURL(blob));

    // 🔥 SAVE IMMEDIATELY TO INDEXEDDB
    await saveProfilePhoto(blob);
  };

  return (
    <div style={overlay}>
      <div style={card}>
        {/* TITLE */}
        <div style={title}>SpeedDigits Trainer</div>
        <div style={subtitle}>Set up your learning profile to get started.</div>

        {/* ABOUT */}
        <div style={about}>
          SpeedDigits Trainer is an interactive arithmetic practice tool
          designed to improve calculation speed, accuracy, and consistency
          through structured, timed practice sessions.
        </div>

        {/* PROFILE IMAGE */}
        <div style={avatarWrap} onClick={() => fileRef.current.click()}>
          <div
            style={{
              ...avatar,
              background: photo
                ? `url(${photo}) center/cover`
                : "rgba(124,58,237,0.15)",
            }}
          >
            {!photo && <span style={{ fontSize: 12 }}>Add Photo</span>}
            <span style={pen}>✏️</span>
          </div>
          <input
            type="file"
            accept="image/*"
            ref={fileRef}
            onChange={onPhotoChange}
            style={{ display: "none" }}
          />
          <div style={optional}>(Optional)</div>
        </div>

        {/* FORM */}
        <div style={{ marginTop: 20 }}>
          <label style={label}>
            Name <span style={req}>*</span>
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle(error && !name)}
            placeholder="Enter your name"
          />
          <label style={label}>
            Class <span style={req}>*</span>
            {/* INFO ICON */}
            <span
              onMouseEnter={
                !isTouchDevice ? () => setShowClassInfo(true) : undefined
              }
              onMouseLeave={
                !isTouchDevice ? () => setShowClassInfo(false) : undefined
              }
              onClick={
                isTouchDevice ? () => setShowClassInfo((v) => !v) : undefined
              }
              style={infoIcon}
            >
              ⓘ
              {showClassInfo && (
                <div style={infoBox} onClick={() => setShowClassInfo(false)}>
                  <div>0 → Pre-school (KG / UKG)</div>
                  <div>1–12 → School classes</div>
                  <div>13 → Dropper</div>
                  <div>14 → College / Higher</div>
                </div>
              )}
            </span>
          </label>

          <input
            type="text"
            inputMode="numeric"
            placeholder="Enter class (0–14)"
            value={userClass}
            onChange={(e) => {
              let val = e.target.value;

              // allow empty (delete works correctly)
              if (val === "") {
                setUserClass("");
                setError(false);
                return;
              }

              // only digits allowed
              if (!/^\d+$/.test(val)) return;

              // convert to number
              const num = Number(val);

              // range check
              if (num < 0 || num > 14) return;

              // normalize value (prevents 01, 001)
              setUserClass(String(num));
              setError(false);
            }}
            style={inputStyle(error && userClass === "")}
          />

          <label style={label}>
            Gender <span style={req}>*</span>
          </label>
          <select
            value={gender}
            onChange={(e) => setGender(e.target.value)}
            style={{
              ...inputStyle(error && !gender),
              appearance: "none",
              WebkitAppearance: "none",
              MozAppearance: "none",
            }}
          >
            <option value="">Select</option>
            <option>Male</option>
            <option>Female</option>
            <option>Other</option>
            <option>Prefer not to say</option>
          </select>

          <label style={label}>
            Date of Birth <span style={opt}>(Optional)</span>
          </label>
          <input
            type="date"
            value={dob}
            onChange={(e) => {
              const value = e.target.value;
              if (!value) {
                setDob("");
                setDobError("");
                return;
              }

              const selected = new Date(value);
              const today = new Date();
              today.setHours(0, 0, 0, 0);

              if (selected > today) {
                setDobError(
                  `Date must be ${today.toLocaleDateString("en-GB")} or earlier`
                );
                return;
              }

              setDob(value);
              setDobError("");
            }}
            style={inputStyle(!!dobError)}
            min="1970-01-01"
            max={new Date().toISOString().split("T")[0]}
          />
          {/* ⬇️ THIS IS THE WHITE BACKGROUND + BOLD PART */}
          {dobError && (
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                fontWeight: 700, // ✅ BOLD TEXT
                color: "#dc2626",
                background: "#ffffff", // ✅ WHITE BACKGROUND
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid rgba(220,38,38,0.35)",
              }}
            >
              {dobError}
            </div>
          )}

          <label style={label}>
            Email <span style={opt}>(Optional)</span>
          </label>
          <input
            value={email}
            onChange={(e) => {
              const value = e.target.value;
              setEmail(value);

              if (!value) {
                setEmailError("");
                return;
              }

              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

              if (!emailRegex.test(value)) {
                setEmailError("Please enter a valid email address");
              } else {
                setEmailError("");
              }
            }}
            style={inputStyle(!!emailError)}
            placeholder="Enter your email   ( example@gmail.com ) "
          />

          {emailError && (
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#dc2626",
                background: "#ffffff",
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid rgba(220,38,38,0.35)",
              }}
            >
              {emailError}
            </div>
          )}
        </div>

        {/* ERROR */}
        {error && (
          <div style={errorText}>
            Please fill all required fields to continue.
          </div>
        )}

        {/* PRIVACY */}
        <div style={privacy}>
          🔒 Your learning data is stored securely on this device. We do not
          share or sell your personal information. You can clear or reset your
          data anytime from Settings.
        </div>

        {/* ACTION */}
        <button style={button} onClick={submit}>
          Save & Start Practicing
        </button>

        {/* FOOTER */}
        <div style={footer}>© SpeedDigits Trainer. All rights reserved.</div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(255,255,255,0.85)",
  backdropFilter: "blur(24px)",

  display: "flex",
  justifyContent: "center",

  /* 🔥 MOBILE FIXES */
  alignItems: "flex-start",
  overflowY: "auto",
  padding: "16px 0",

  zIndex: 9999,
};

const card = {
  width: "100%",
  maxWidth: 520,
  margin: "0 16px",
  background: "#ffffff",
  borderRadius: 20,

  /* 🔥 MOBILE SAFE */
  maxHeight: "calc(100dvh - 32px)",
  overflowY: "auto",

  padding: "22px 20px",
  border: "1px solid rgba(15,23,42,0.25)",
  boxShadow: "0 40px 80px rgba(15,23,42,0.25)",
};

const title = {
  fontSize: "clamp(32px, 5vw, 42px)",
  fontWeight: 900,
  textAlign: "center",
  background: "linear-gradient(135deg,#7c3aed,#ec4899)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const subtitle = {
  textAlign: "center",
  fontSize: 15,
  color: "#475569",
  marginTop: 6,
};

const avatarWrap = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  marginTop: 14,
};

const avatar = {
  width: 90,
  height: 90,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  position: "relative",
};

const pen = {
  position: "absolute",
  bottom: 4,
  right: 4,
  background: "rgba(0,0,0,0.6)",
  color: "#fff",
  padding: 6,
  borderRadius: "50%",
  fontSize: 14,
};

const optional = {
  fontSize: 12,
  color: "#64748b",
  marginTop: 6,
};

const about = {
  fontSize: 14,
  color: "#475569",
  marginTop: 18,
  lineHeight: 1.6,
};

const label = {
  display: "block",
  marginTop: 16,
  marginBottom: 6,
  fontWeight: 700,
  color: "#334155",
};

const req = { color: "#ef4444" };
const opt = { color: "#64748b" };

const errorText = {
  marginTop: 14,
  color: "#ef4444",
  fontWeight: 600,
  textAlign: "center",
};

const privacy = {
  fontSize: 12.5,
  color: "#475569",
  marginTop: 18,
};

const button = {
  marginTop: 18,
  marginBottom: 6,
  width: "100%",
  padding: 16,
  fontSize: 16,
  fontWeight: 700,
  borderRadius: 14,
  border: "none",
  cursor: "pointer",
  color: "#fff",
  background: "linear-gradient(135deg,#7c3aed,#9333ea)",
  boxShadow: "0 0 20px rgba(124,58,237,0.55)",
};

const footer = {
  marginTop: 18,
  textAlign: "center",
  fontSize: 12,
  color: "#64748b",
  borderTop: "1px dotted rgba(15,23,42,0.25)",
  paddingTop: 10,
};
const infoBox = {
  position: "absolute",
  top: 26,
  left: "50%",
  transform: "translateX(-50%)",

  background: "#ffffff",
  color: "#334155",

  padding: "10px 12px",
  borderRadius: 12,
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "nowrap",

  border: "1px solid rgba(124,58,237,0.25)",
  boxShadow: "0 12px 30px rgba(124,58,237,0.25)",

  zIndex: 20,
};

const FIELD_WIDTH = 440; // uniform width for all form fields

const inputStyle = (invalid) => ({
  width: "100%",
  maxWidth: "100%",
  display: "block",
  boxSizing: "border-box",

  padding: "14px 16px",
  borderRadius: 12,
  border: invalid ? "2px solid #ef4444" : "1px solid rgba(15,23,42,0.2)",
  fontSize: 15,
  outline: "none",
  boxShadow: invalid ? "0 0 0 3px rgba(239,68,68,0.25)" : "none",
});

const infoIcon = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: 8,
  width: 18,
  height: 18,
  borderRadius: "50%",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  color: "#7c3aed",
  background: "rgba(124,58,237,0.12)",
  position: "relative",
};
